import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Stack,
  useTheme,
  Link,
  IconButton,
  TextField,
  Button,
  Grid
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LinkedIn
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const TermsOfService = () => {
  const theme = useTheme();

  const sections = [
    {
      title: "Acceptance of Terms",
      content: [
        "By accessing and using EthioReview's website and services, you accept and agree to be bound by the terms and provision of this agreement.",
        "If you do not agree to abide by the above, please do not use this service.",
        "These terms apply to all visitors, users, and others who access or use the service.",
        "We reserve the right to modify these terms at any time, and such modifications shall be effective immediately upon posting."
      ]
    },
    {
      title: "Use License",
      content: [
        "Permission is granted to temporarily download one copy of the materials on EthioReview's website for personal, non-commercial transitory viewing only.",
        "This is the grant of a license, not a transfer of title, and under this license you may not:",
        "• Modify or copy the materials",
        "• Use the materials for any commercial purpose or for any public display",
        "• Attempt to reverse engineer any software contained on the website",
        "• Remove any copyright or other proprietary notations from the materials"
      ]
    },

    {
      title: "Content and Conduct",
      content: [
        "You agree not to use the service to:",
        "• Post, upload, or transmit any content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable",
        "• Impersonate any person or entity or misrepresent your affiliation with any person or entity",
        "• Interfere with or disrupt the service or servers connected to the service",
        "• Attempt to gain unauthorized access to any portion of the service or any systems or networks",
        "• Use any automated means to access the service or collect information from the service",
        "• Submit false or misleading information through our contact forms"
      ]
    },
    {
      title: "Intellectual Property",
      content: [
        "The service and its original content, features, and functionality are and will remain the exclusive property of EthioReview and its licensors.",
        "The service is protected by copyright, trademark, and other laws.",
        "Our trademarks and trade dress may not be used in connection with any product or service without our prior written consent.",
        "You may share our content through social media and other platforms, provided you give proper attribution to EthioReview."
      ]
    },
    {
      title: "Privacy Policy",
      content: [
        "Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the service.",
        "The Privacy Policy explains how we collect, use, and protect your personal information.",
        "By using our service, you consent to the collection and use of information in accordance with our Privacy Policy.",
        "We may update our Privacy Policy from time to time, and we will notify you of any changes."
      ]
    },
    {
      title: "Disclaimer",
      content: [
        "The materials on EthioReview's website are provided on an 'as is' basis.",
        "EthioReview makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including without limitation:",
        "• Implied warranties of merchantability or fitness for a particular purpose",
        "• Non-infringement of intellectual property or other violation of rights",
        "• Accuracy, completeness, or reliability of any content",
        "• Availability or uninterrupted access to the service"
      ]
    },
    {
      title: "Limitations",
      content: [
        "In no event shall EthioReview or its suppliers be liable for any damages arising out of the use or inability to use the materials on the website.",
        "This includes but is not limited to damages for loss of data or profit, or due to business interruption.",
        "Some jurisdictions do not allow limitations on implied warranties or limitations of liability for consequential or incidental damages.",
        "These limitations may not apply to you if your jurisdiction does not allow such limitations."
      ]
    },
    {
      title: "Accuracy of Materials",
      content: [
        "The materials appearing on EthioReview's website could include technical, typographical, or photographic errors.",
        "EthioReview does not warrant that any of the materials on its website are accurate, complete, or current.",
        "EthioReview may make changes to the materials contained on its website at any time without notice.",
        "However, EthioReview does not make any commitment to update the materials."
      ]
    },
    {
      title: "Links",
      content: [
        "EthioReview has not reviewed all of the sites linked to its website and is not responsible for the contents of any such linked site.",
        "The inclusion of any link does not imply endorsement by EthioReview of the site.",
        "Use of any such linked website is at the user's own risk.",
        "We strongly advise you to read the terms and conditions and privacy policies of any third-party websites that you visit."
      ]
    },
    {
      title: "Modifications",
      content: [
        "EthioReview may revise these terms of service for its website at any time without notice.",
        "By using this website, you are agreeing to be bound by the then current version of these terms of service.",
        "We will notify users of any material changes to these terms.",
        "Your continued use of the service after any changes constitutes acceptance of the new terms."
      ]
    },
    {
      title: "Governing Law",
      content: [
        "These terms and conditions are governed by and construed in accordance with the laws of Ethiopia.",
        "You hereby irrevocably submit to the exclusive jurisdiction of the courts in Ethiopia.",
        "Any disputes arising from these terms or your use of the service will be resolved in Ethiopian courts.",
        "If any provision of these terms is found to be unenforceable, the remaining provisions will remain in full force and effect."
      ]
    }
  ];

  return (
    <>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            py: 6,
            position: 'relative'
          }}
        >
          <Container maxWidth="lg">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                variant="h2"
                component="h1"
                align="center"
                sx={{
                  fontWeight: 'bold',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Terms of Service
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{
                  opacity: 0.9,
                  maxWidth: '800px',
                  mx: 'auto'
                }}
              >
                Please read these terms carefully before using our services
              </Typography>
            </motion.div>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: 6 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                These Terms of Service govern your use of the EthioReview website and services. 
                By accessing or using our services, you agree to be bound by these terms. 
                If you disagree with any part of these terms, then you may not access our services.
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Last updated:</strong> {new Date().toLocaleDateString()}
              </Typography>
            </Paper>
          </motion.div>

          {/* Terms Sections */}
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
            >
              <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  gutterBottom
                  sx={{ 
                    fontWeight: 'bold', 
                    color: 'primary.main', 
                    mb: 3,
                    fontSize: { xs: '1.5rem', md: '1.75rem' }
                  }}
                >
                  {section.title}
                </Typography>
                <Stack spacing={2}>
                  {section.content.map((item, itemIndex) => (
                    <Typography 
                      key={itemIndex} 
                      variant="body1" 
                      sx={{ 
                        lineHeight: 1.7,
                        pl: item.startsWith('•') ? 0 : 2,
                        borderLeft: item.startsWith('•') ? 'none' : `3px solid ${theme.palette.primary.light}`,
                        ml: item.startsWith('•') ? 2 : 1
                      }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Stack>
              </Paper>
            </motion.div>
          ))}

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.5 }}
          >
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Contact Us
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3 }}>
                If you have any questions about these Terms of Service, please contact us:
              </Typography>
              <Stack spacing={2} alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">legal@ethioreview.com</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body1">123 News Street, Addis Ababa, Ethiopia</Typography>
                </Box>
              </Stack>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          backgroundColor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Quick Links */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Quick Links
              </Typography>
              <Stack spacing={1}>
                {[
                  { name: "Home", path: "/" },
                  { name: "About Us", path: "/about" },
                  { name: "Contact", path: "/contact" }
                ].map((item) => (
                  <motion.div whileHover={{ x: 5 }} key={item.name}>
                    <Link
                      to={item.path}
                      style={{
                        display: "block",
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Categories */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Categories
              </Typography>
              <Stack spacing={1}>
                {["Politics", "Business", "Sports"].map((category) => (
                  <motion.div whileHover={{ x: 5 }} key={category}>
                    <Link
                      to={`/${category.toLowerCase()}-news`}
                      style={{
                        display: "block",
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "primary.main" },
                      }}
                    >
                      {category}
                    </Link>
                  </motion.div>
                ))}
              </Stack>
            </Grid>

            {/* Contact Info */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Contact Us
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    123 News Street, Addis Ababa, Ethiopia
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">+251 123 456 789</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Email color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2">info@ethioreview.com</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Social Media & Newsletter */}
            <Grid xs={12} sm={6} md={3}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Follow Us
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
                  { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
                  { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
                  { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
                  { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
                ].map((social) => (
                  <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                    <IconButton
                      href="#"
                      target="_blank"
                      rel="noopener"
                      sx={{
                        backgroundColor: `${social.color}10`,
                        color: social.color,
                        "&:hover": { backgroundColor: `${social.color}20` },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  </motion.div>
                ))}
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Newsletter
              </Typography>
              <Box component="form" sx={{ display: "flex" }}>
                <TextField
                  size="small"
                  placeholder="Your email"
                  variant="outlined"
                  fullWidth
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Subscribe
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* Copyright & Legal */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} EthioReview. All rights reserved.
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
              {[
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "Cookie Policy", path: "/cookies" }
              ].map((item) => (
                <Typography
                  key={item.name}
                  component={Link}
                  to={item.path}
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {item.name}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default TermsOfService; 